import React, { useState, useEffect, lazy } from 'react';
import SimLoading from './components/SimLoading';
import MainPortal from './components/MainPortal';
import LicenseGate from './components/LicenseGate';
import LicenseStatusBadge from './components/LicenseStatusBadge';
import { simulatorRegistry } from './utils/simulatorRegistry';
import { isFreeTrial, isTrainingOnlyCode } from './utils/licenseService';
import { setPlaybackPaused } from './lib/playback-control';
import useIsMobile from './hooks/useIsMobile';
import useEngagement from './hooks/useEngagement';
import useLessonGate from './hooks/useLessonGate';
import { setLocation } from './lib/engagement';

// 첫 화면(라이선스 코드 입력)에는 아래 화면이 하나도 필요 없다. 정적으로 import 하면
// 코스 대본·주제 서고·정비 트랙까지 전부 첫 번들에 실려, 코드 한 줄 넣으려는 사람이
// 수 MB 를 기다린다 — 필요할 때 받는다. 그리는 자리는 <SimLoading>(Suspense) 안이다.
const CourseMode = lazy(() => import('./components/CourseMode'));
const GuidedCourseMode = lazy(() => import('./components/GuidedCourseMode'));
const MaintenanceDay1 = lazy(() => import('./components/maintenance/MaintenanceDay1'));
const MaintenanceTrack = lazy(() => import('./components/maintenance/MaintenanceTrack'));
const MatrixPrototype = lazy(() => import('./components/MatrixPrototype'));
const TopicModuleBrowser = lazy(() => import('./components/TopicModuleBrowser'));
const TopicLibrary = lazy(() => import('./components/TopicLibrary'));
// 직무 서고 미리보기 — JOB_CHAPTERS 를 여기서 import 하면 첫 번들이 직무 책장
// 데이터까지 지고 간다. 문을 여는 사람만 받도록 컴포넌트째 떼어 놨다.
const JobStacksPreview = lazy(() => import('./components/JobStacksPreview'));

const previewParam =
  typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('preview')
    : null;

// 프리뷰(?preview=…)는 LicenseGate 밖의 무인증 창이라, 공개 URL 유출을 막기 위해
// 토큰(?k=…)을 요구한다. 토큰이 없거나 틀리면 프리뷰 분기를 안 타고 일반 앱으로 폴백.
//  ⚠️ 클라 토큰은 번들에 노출됨 — "캐주얼 유출 차단"용이지 완전 보안 아님(진짜 차단은 서버측).
const PREVIEW_KEY = 'sfab-preview-9x2k7';
const previewKeyOk =
  typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('k') === PREVIEW_KEY
    : false;

const prototypeParam =
  typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('prototype')
    : null;

// ?legacy=1 — 구 UI (센터 기반 코스 선택 화면) 보존 옵션.
// 디폴트 진입 (kredu.semifabai.com) 은 새 4-카드 프로토타입 매트릭스이며,
// 구 센터 UI 가 필요한 사용자만 ?legacy=1 로 접근.
const legacyParam =
  typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('legacy')
    : null;

// 시뮬레이터 ID와 Day 매핑
const simulatorToDayMap = {
  vacuum: 1,
  cleaning: 2,
  oxidation: 3,
  lithography: 4,
};

const App = () => {
  const isMobile = useIsMobile();
  // 모드: 'course' | 'simulator' | 'guided' | 'matrix-prototype'
  // 기본 진입(쿼리 없음) → matrix-prototype (4개 코스 카드).
  // ?legacy=1     → course (구 센터 기반 UI). 보존용 — 일반 사용자는 안 봄.
  // ?prototype=1  → matrix-prototype (이전 명시 호출도 동일하게 작동)
  // 무료 체험은 그대로 guided 직행.
  const [mode, setModeRaw] = useState(() => {
    if (isTrainingOnlyCode()) return 'matrix-prototype';   // 개인 미션 코드 — 옛 화면(?legacy=1)으로도 못 샌다
    if (legacyParam === '1') return 'course';
    if (isFreeTrial()) return 'guided';
    return 'matrix-prototype';
  });
  // 개인 미션 코드는 첫 화면(포털) 밖으로 나가는 모든 전환을 무시한다 — 문이 여럿이라(Day 시작·시뮬 열기·
  //  뒤로가기) 하나씩 막으면 새로 생긴 문에서 샌다. 전환이 모이는 한 곳에서 막는다.
  const setMode = (next) => setModeRaw(isTrainingOnlyCode() ? 'matrix-prototype' : next);
  const [activeSimulator, setActiveSimulator] = useState('vacuum');

  // NPC 가이드 모드 상태
  // null = 특정 Day 의도 없음 → GuidedCourseMode가 진행도로 자동 이어하기
  // 숫자 = 사용자가 명시적으로 선택한 Day (해당 Day로 점프)
  const [guidedDay, setGuidedDay] = useState(null);
  const [guidedCourseType, setGuidedCourseType] = useState(() => isFreeTrial() ? '3day' : 'expert'); // 'intro' | 'standard' | 'expert'
  const [guidedFreeExplore, setGuidedFreeExplore] = useState(false); // 자유 학습 모드: 모든 Day/탭 잠금 해제

  // 강의자가 담은 Day 만 열린다. 학습 화면에도 Day 로 들어가는 문이 둘 더 있어서
  // (다음 Day 하기 · Day 네비게이터) 매트릭스에서만 막으면 그대로 새어 나간다.
  const lessonGate = useLessonGate();

  // 현재 활성화된 시뮬레이터 컴포넌트 가져오기
  const CurrentSimulator = simulatorRegistry.getSimulator(activeSimulator);

  // 기관 좌석 engagement 추적(접속/체류시간/위치) — 앱 루트 1회 마운트.
  // 기관 좌석(CLS-) 이 아니면 내부에서 자동 no-op.
  useEngagement(true);

  // 현재 "위치" 보고 — guided 모드 내부 위치는 GuidedCourseMode 가 직접 보고하므로
  // 여기서는 홈/자유 시뮬레이터만 처리한다.
  useEffect(() => {
    if (mode === 'guided') return;
    if (mode === 'simulator') {
      const info = simulatorRegistry.getSimulatorInfo(activeSimulator);
      setLocation(`sim:${activeSimulator}`, info?.name || activeSimulator);
    } else {
      setLocation('home', '코스 선택');
    }
  }, [mode, activeSimulator]);

  // 코스 모드에서 시뮬레이터 열기
  const handleOpenSimulator = (simulatorId, preset) => {
    setActiveSimulator(simulatorId);
    setMode('simulator');
    console.log('Opening simulator:', simulatorId, 'with preset:', preset);
  };

  // 가이드 모드 시작 (3일/7일/14일 또는 자유 탐색)
  const handleStartGuidedMode = (courseType = 'expert') => {
    setMode('guided');
    setActiveSimulator('vacuum'); // Day 1은 Vacuum
    // null = "특정 Day 지정 없이 코스 진입" → 진행도가 있으면 자동 이어하기
    setGuidedDay(null);
    // 'free' 코스는 14일 콘텐츠를 자유 탐색 모드로 진입
    if (courseType === 'free') {
      setGuidedCourseType('14day');
      setGuidedFreeExplore(true);
    } else {
      setGuidedCourseType(courseType);
      setGuidedFreeExplore(false);
    }
    // 전 과정(3·7·14일) 음성 완비 → 모든 과정 자동재생. (과거 3일만 음성 있던 시절의
    // "비-3일 일시정지"는 제거 — 그 잔재가 7/14일 진입 시 멈춤 상태로 들어가게 해
    // step 1 이 재생/진행을 못 하던 원인이었다. GuidedCourseMode 도 동일하게 해제한다.)
    setPlaybackPaused(false);
  };

  // 시뮬레이터 선택 시 해당 Day의 가이드 모드로 전환 (코스 모드에서 사용)
  const handleSimulatorSelectForGuided = (simulatorId) => {
    const day = simulatorToDayMap[simulatorId];
    if (day) {
      setMode('guided');
      setActiveSimulator(simulatorId);
      setGuidedDay(day);
    } else {
      // 매핑된 Day가 없으면 자유 시뮬레이션 모드
      setActiveSimulator(simulatorId);
      setMode('simulator');
    }
  };

  // 자유 시뮬레이션 모드에서 시뮬레이터 선택 (모드 전환 없이)
  const handleSimulatorSelectFree = (simulatorId) => {
    setActiveSimulator(simulatorId);
  };

  // 모바일 사이드바 토글
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // 로그아웃 핸들러
  const handleLogout = () => {
    window.location.reload();
  };

  // 프리뷰 URL 은 검색엔진 색인 제외(공개 노출 최소화). 프리뷰 파라미터가 있으면 noindex.
  useEffect(() => {
    if (!previewParam) return undefined;
    const m = document.createElement('meta');
    m.name = 'robots';
    m.content = 'noindex,nofollow';
    document.head.appendChild(m);
    return () => { try { document.head.removeChild(m); } catch (e) { /* 무시 */ } };
  }, []);

  if (previewKeyOk && previewParam === 'maint-day1') {
    return (
      <div className="h-screen overflow-auto bg-slate-950">
        <SimLoading label="화면을 준비하고 있습니다"><MaintenanceDay1 /></SimLoading>
      </div>
    );
  }

  if (previewKeyOk && previewParam === 'maint-track') {
    return <SimLoading label="화면을 준비하고 있습니다"><MaintenanceTrack /></SimLoading>;
  }

  // 주제 모듈(기관용 강의자 교안 도구) 미리보기 — 정식 노출은 base.topicModule + 기관용 뷰 게이팅.
  if (previewKeyOk && previewParam === 'topic-modules') {
    return (
      <SimLoading label="교재를 준비하고 있습니다">
        <TopicModuleBrowser
          onLaunchSim={(simId, tab) => console.log('launch sim:', simId, tab)}
        />
      </SimLoading>
    );
  }

  // 주제 서고(대형 원형 도서관 스타일) 미리보기 — topic-modules 의 대안 내비게이션.
  if (previewKeyOk && previewParam === 'topic-stacks') {
    return (
      <SimLoading label="교재를 준비하고 있습니다">
        <TopicLibrary
          onLaunchSim={(simId, tab) => console.log('launch sim:', simId, tab)}
        />
      </SimLoading>
    );
  }

  // 직무 트랙의 서고 미리보기 — 직무 책장이 직무별로 갈리고 주제 책장은 뒤에 선다.
  //  운영사 콘솔이 직무 슬롯(job.*)을 켠 기관의 학생이 보는 화면이다. 좌석 없이
  //  같은 화면을 보려고 둔 문이라 실제 동작과 어긋나면 안 된다 — 프롭이 같다.
  if (previewKeyOk && previewParam === 'job-stacks') {
    return (
      <SimLoading label="교재를 준비하고 있습니다">
        <JobStacksPreview />
      </SimLoading>
    );
  }



  return (
    <LicenseGate>
      <div className="flex flex-col h-screen bg-slate-950">
        {/* Top Navigation Bar */}
        <div className={`bg-gradient-to-r from-slate-900 to-slate-950 text-white ${isMobile ? 'px-3 py-2' : 'px-6 py-3'} flex items-center justify-between shadow-lg border-b border-slate-800`}>
          <div className="flex items-center space-x-3 md:space-x-6 min-w-0">
            {/* Logo */}
            <div className="flex items-center space-x-2 min-w-0">
              <span className={isMobile ? 'text-lg' : 'text-2xl'}>🔬</span>
              {isMobile ? (
                <span className="font-bold text-sm truncate">반도체 공정 시뮬</span>
              ) : (
                <span className="font-bold text-lg">Semiconductor Process Simulator</span>
              )}
              <span className="text-xs bg-slate-700 px-1.5 py-0.5 rounded text-slate-300 flex-shrink-0">V67</span>
            </div>

            {/* Home button (when not in course selection, disabled for free trial) */}
            {mode !== 'course' && mode !== 'matrix-prototype' && !isFreeTrial() && (
              <button
                onClick={() => setMode(legacyParam === '1' ? 'course' : 'matrix-prototype')}
                className="flex items-center space-x-1 px-2 py-1 md:space-x-1.5 md:px-3 md:py-1.5 rounded-md text-sm font-medium
                           text-slate-400 hover:text-white hover:bg-slate-700 transition-colors flex-shrink-0"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                {!isMobile && <span>Home</span>}
              </button>
            )}
          </div>

          {/* Right side info */}
          <div className="flex items-center space-x-2 md:space-x-4 text-sm text-slate-400 flex-shrink-0">
            <LicenseStatusBadge onLogout={handleLogout} />
            {!isMobile && mode !== 'matrix-prototype' && <span>Educational Simulator</span>}
            {/* 첫 화면 오른쪽에 있던 「PROTOTYPE」 배지를 뺐다(2026-09-17 담당자).
                파는 화면에 "시제품"이라고 적혀 있으면 학생·기관은 그 말을 그대로 읽는다.
                ★내부 이름(mode 'matrix-prototype' · ?prototype=1 · recording-mode)은 그대로 둔다 —
                 화면에 안 나오는 식별자고, 고치면 라우팅·녹화 모드까지 번진다. */}
            {!isMobile && mode === 'simulator' && (
              <span className="text-blue-400">
                Current: {simulatorRegistry.getSimulatorInfo(activeSimulator)?.name || 'Simulator'}
              </span>
            )}
          </div>
        </div>

        {/* 메인 콘텐츠 영역 */}
        <div className="flex-1 flex overflow-hidden relative">
          {/* 화면 본체는 필요할 때 받는다(React.lazy). 받는 동안 상단 바는 그대로 두고
              이 안쪽만 자리표시자가 된다 — 화면 전체를 감싸면 통째로 사라진다. */}
          <SimLoading label="화면을 준비하고 있습니다">
          {mode === 'matrix-prototype' ? (
            /* 매트릭스 프로토타입 (?prototype=1) */
            <MatrixPrototype
              onStartGuidedDay={(courseType, day) => {
                setGuidedCourseType(courseType);
                setGuidedDay(day);
                setGuidedFreeExplore(false);
                // 전 과정 음성 완비 → 모든 과정 자동재생 (비-3일 일시정지 잔재 제거: 멈춤 원인)
                setPlaybackPaused(false);
                setMode('guided');
              }}
              onOpenSimulator={(simulatorId) => {
                setActiveSimulator(simulatorId);
                setMode('simulator');
              }}
            />
          ) : mode === 'course' ? (
            /* 코스 학습 모드 - 코스 선택 화면 */
            <CourseMode
              onOpenSimulator={handleOpenSimulator}
              onStartGuidedMode={handleStartGuidedMode}
              isMobile={isMobile}
            />
          ) : mode === 'guided' ? (
            /* 가이드 모드 (7일/14일/30일) */
            <GuidedCourseMode
              onBack={() => setMode(legacyParam === '1' ? 'course' : 'matrix-prototype')}
              initialDay={guidedDay}
              courseType={guidedCourseType}
              onDayChange={setGuidedDay}
              isMobile={isMobile}
              initialFreeExploreMode={guidedFreeExplore}
              allowedDays={lessonGate.allowedDays(guidedCourseType)}
            />
          ) : (
            /* 자유 시뮬레이션 모드 */
            <>
              {/* 모바일: 사이드바 오버레이 */}
              {isMobile && (
                <div
                  className={`mobile-sidebar-overlay ${mobileSidebarOpen ? 'active' : ''}`}
                  onClick={() => setMobileSidebarOpen(false)}
                />
              )}

              {/* 좌측 사이드바 */}
              {isMobile ? (
                <div className={`mobile-sidebar bg-slate-900 ${mobileSidebarOpen ? 'open' : ''}`}>
                  <MainPortal
                    activeSimulator={activeSimulator}
                    onSimulatorChange={(id) => {
                      handleSimulatorSelectFree(id);
                      setMobileSidebarOpen(false);
                    }}
                  />
                </div>
              ) : (
                <MainPortal
                  activeSimulator={activeSimulator}
                  onSimulatorChange={handleSimulatorSelectFree}
                />
              )}

              {/* 메인 컨텐츠 영역 */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* 모바일 햄버거 버튼 */}
                {isMobile && (
                  <div className="bg-slate-900 border-b border-slate-700 px-3 py-2 flex items-center gap-2">
                    <button
                      onClick={() => setMobileSidebarOpen(true)}
                      className="p-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                    </button>
                    <span className="text-sm text-slate-300 font-medium truncate">
                      {simulatorRegistry.getSimulatorInfo(activeSimulator)?.name || '시뮬레이터 선택'}
                    </span>
                  </div>
                )}
                {/* 시뮬레이터 콘텐츠 - 스크롤 가능 */}
                <div className="flex-1 overflow-auto">
                  {CurrentSimulator ? (
                    <SimLoading><CurrentSimulator /></SimLoading>
                  ) : (
                    <div className="flex-1 flex items-center justify-center h-full">
                      <div className="text-center">
                        <h2 className="text-2xl font-bold text-slate-200 mb-4">
                          Preparing Simulator
                        </h2>
                        <p className="text-slate-400">
                          The selected simulator will be ready shortly.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </>
          )}
          </SimLoading>
        </div>
      </div>
    </LicenseGate>
  );
};

export default App;
